import React from "react";
import {
  AbsoluteFill,
  interpolate,
  Sequence,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { C, FONT, INFO, SERVICES, bd, rp } from "./brand";
import { useOnestFonts } from "./fonts";
import { Card, Chip, Counter, In, Pole, SceneHeading, ServiceRow } from "./parts";

const FPS = 30;
export const TOTAL = 590;

// waktu per scene (frame), 30fps
export const T = {
  title: 0,
  kasir: 95,
  nota: 265,
  laporan: 430,
  cta: 525,
};

const dots = (color: string, size = 26, opacity = 0.08) => ({
  backgroundImage: `radial-gradient(circle, ${color} 1px, transparent 1px)`,
  backgroundSize: `${size}px ${size}px`,
  opacity,
});

const Frame: React.FC<{
  children: React.ReactNode;
  bg?: string;
  style?: React.CSSProperties;
}> = ({ children, bg = C.cream, style }) => (
  <AbsoluteFill
    style={{ background: bg, fontFamily: FONT, color: C.ink, ...style }}
  >
    <AbsoluteFill style={{ ...dots(C.ink), pointerEvents: "none" }} />
    {children}
  </AbsoluteFill>
);

/* ---------------------------------------------------------------- scene 1 */
const Title: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const pop = spring({ frame, fps, config: { damping: 11, stiffness: 110 } });
  const scale = interpolate(pop, [0, 1], [0.7, 1]);
  const poleIn = spring({ frame: frame - 12, fps, config: { damping: 13 } });
  const poleW = interpolate(poleIn, [0, 1], [0, 58]);

  return (
    <Frame>
      <AbsoluteFill style={{ justifyContent: "center", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 30 }}>
          <Pole height={250} width={Math.max(1, poleW)} />
          <div style={{ textAlign: "center", transform: `scale(${scale})` }}>
            <div
              style={{
                fontSize: 178,
                fontWeight: 800,
                textTransform: "uppercase",
                letterSpacing: -8,
                lineHeight: 1,
              }}
            >
              {INFO.name}
            </div>
            <div
              style={{
                fontSize: 30,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: 14,
                color: C.ink600,
                marginTop: 10,
              }}
            >
              {INFO.sub}
            </div>
          </div>
          <Pole height={250} width={Math.max(1, poleW)} />
        </div>

        <In delay={18} style={{ marginTop: 64 }}>
          <Chip bg={C.yellow} size={24}>
            {INFO.tagline}
          </Chip>
        </In>

        <In delay={26} style={{ marginTop: 34, display: "flex", gap: 14 }}>
          <Chip bg={C.blue} size={20}>
            Kasir / POS
          </Chip>
          <Chip bg={C.pink} size={20}>
            Nota WhatsApp
          </Chip>
          <Chip bg={C.lime} size={20}>
            Poin Member
          </Chip>
        </In>
      </AbsoluteFill>
    </Frame>
  );
};

/* ---------------------------------------------------------------- scene 2 */
const Kasir: React.FC = () => {
  const total = SERVICES.reduce((a, s) => a + s.price, 0);
  const bonus = Math.round(total * 0.2);

  return (
    <Frame>
      <AbsoluteFill style={{ padding: 54, alignItems: "center" }}>
        <SceneHeading
          kicker="Kasir"
          kickerBg={C.blue}
          title="Catat transaksi, hitung bonus otomatis"
          size={58}
        />

        <In delay={20} style={{ width: 900, marginTop: 36 }}>
          <Card shadow={9} padding={26}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 18,
              }}
            >
              <div style={{ fontSize: 30, fontWeight: 800, textTransform: "uppercase" }}>
                Meja 2 · Rizky
              </div>
              <Chip bg={C.cream} size={20}>
                6 pelanggan hari ini
              </Chip>
            </div>

            {SERVICES.map((s, i) => (
              <ServiceRow
                key={s.name}
                name={s.name}
                price={rp(s.price)}
                index={i}
                startAt={34}
                accent={i % 2 === 0 ? C.blue : C.lime}
              />
            ))}

            <div
              style={{
                borderTop: `3px dashed ${C.ink}`,
                marginTop: 18,
                paddingTop: 18,
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "space-between",
              }}
            >
              <div>
                <div style={{ fontSize: 24, fontWeight: 700, color: C.ink600 }}>
                  TOTAL
                </div>
                <div style={{ fontSize: 62, fontWeight: 800, letterSpacing: -2 }}>
                  <Counter to={total} delay={72} durationInFrames={40} format={rp} />
                </div>
              </div>
              <Chip bg={C.yellow} size={20}>
                Bonus karyawan 20% = {rp(bonus)}
              </Chip>
            </div>

            <In delay={96} from="bottom" style={{ marginTop: 22 }}>
              <div
                style={{
                  background: C.yellow,
                  border: bd(4),
                  borderRadius: 16,
                  boxShadow: `5px 5px 0 ${C.ink}`,
                  padding: "20px 0",
                  textAlign: "center",
                  fontSize: 34,
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: 3,
                }}
              >
                Bayar sekarang →
              </div>
            </In>
          </Card>
        </In>
      </AbsoluteFill>
    </Frame>
  );
};

/* ---------------------------------------------------------------- scene 3 */
const Nota: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const pop = spring({ frame: frame - 12, fps, config: { damping: 13, stiffness: 95 } });
  const sent = interpolate(frame, [86, 100], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const bullets = [
    { t: "Nota dikirim ke WhatsApp pelanggan", bg: C.lime },
    { t: "Poin member bertambah otomatis", bg: C.yellow },
    { t: "Riwayat & pengeluaran tercatat", bg: C.orange },
  ];

  return (
    <Frame>
      <AbsoluteFill style={{ padding: 54, alignItems: "center" }}>
        <SceneHeading
          kicker="Nota otomatis"
          kickerBg={C.lime}
          title="Kirim nota ke WhatsApp pelanggan"
          size={58}
        />

        <div
          style={{
            display: "flex",
            gap: 40,
            alignItems: "center",
            marginTop: 40,
            transform: `scale(${pop})`,
          }}
        >
          <div
            style={{
              width: 470,
              background: C.white,
              border: bd(5),
              borderRadius: 44,
              boxShadow: `10px 10px 0 ${C.ink}`,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                background: C.ink,
                color: C.white,
                padding: "22px 26px",
                display: "flex",
                justifyContent: "space-between",
                fontSize: 26,
                fontWeight: 700,
              }}
            >
              <span>Pangkasin · Nota</span>
              <span style={{ letterSpacing: 3 }}>•••</span>
            </div>

            <div style={{ background: "#e5ddd5", padding: 22, minHeight: 470 }}>
              <div
                style={{
                  background: C.white,
                  border: bd(3),
                  borderRadius: 16,
                  boxShadow: `4px 4px 0 ${C.ink}`,
                  padding: 20,
                  width: 360,
                }}
              >
                <div style={{ fontSize: 24, fontWeight: 700, marginBottom: 12 }}>
                  Halo Andi, terima kasih sudah mampir! 💈
                </div>
                <div
                  style={{
                    border: `2px dashed ${C.ink}`,
                    borderRadius: 10,
                    padding: 14,
                    fontSize: 22,
                    fontWeight: 600,
                  }}
                >
                  <Row label="Potong Rambut" value="Rp35.000" />
                  <Row label="Cukur Jenggot" value="Rp20.000" />
                  <div
                    style={{
                      borderTop: `2px solid ${C.ink}`,
                      marginTop: 8,
                      paddingTop: 8,
                      display: "flex",
                      justifyContent: "space-between",
                      fontWeight: 800,
                    }}
                  >
                    <span>TOTAL</span>
                    <span>Rp55.000</span>
                  </div>
                </div>
                <div
                  style={{
                    marginTop: 12,
                    fontSize: 20,
                    fontWeight: 700,
                    color: C.ink600,
                    textAlign: "right",
                  }}
                >
                  10:41 <span style={{ color: C.ok, opacity: sent }}>✓✓</span>
                </div>
              </div>
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 18, width: 440 }}>
            {bullets.map((b, i) => (
              <In key={b.t} delay={40 + i * 12} from="right">
                <Card padding={22} shadow={7} bg={C.white} style={{ display: "flex", gap: 16, alignItems: "center" }}>
                  <div
                    style={{
                      width: 46,
                      height: 46,
                      borderRadius: 12,
                      background: b.bg,
                      border: bd(3),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 800,
                      fontSize: 26,
                      flexShrink: 0,
                    }}
                  >
                    ✓
                  </div>
                  <span style={{ fontSize: 27, fontWeight: 700, lineHeight: 1.25 }}>{b.t}</span>
                </Card>
              </In>
            ))}
          </div>
        </div>
      </AbsoluteFill>
    </Frame>
  );
};

const Row: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
    <span>{label}</span>
    <span>{value}</span>
  </div>
);

/* ---------------------------------------------------------------- scene 4 */
const Laporan: React.FC = () => {
  const frame = useCurrentFrame();
  const stats = [
    { label: "Omzet hari ini", value: 2450000, bg: C.blue, fmt: rp },
    { label: "Pengeluaran", value: 310000, bg: C.orange, fmt: rp },
    { label: "Member aktif", value: 128, bg: C.pink, fmt: (n: number) => `${Math.round(n)} orang` },
  ];
  const days = ["S", "S", "R", "K", "J", "S", "M"];

  return (
    <Frame>
      <AbsoluteFill style={{ padding: 54, alignItems: "center" }}>
        <SceneHeading
          kicker="Laporan & gaji"
          kickerBg={C.purple}
          title="Semua angka kelihatan, tiap hari"
          size={58}
        />

        <div style={{ display: "flex", gap: 22, marginTop: 40 }}>
          {stats.map((s, i) => (
            <In key={s.label} delay={16 + i * 10} style={{ width: 300 }}>
              <Card padding={24} shadow={8} bg={C.white}>
                <div
                  style={{
                    fontSize: 22,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: 1,
                    color: C.ink600,
                  }}
                >
                  {s.label}
                </div>
                <div
                  style={{
                    fontSize: s.fmt === rp ? 44 : 46,
                    fontWeight: 800,
                    letterSpacing: -2,
                    marginTop: 10,
                  }}
                >
                  <Counter to={s.value} delay={34 + i * 6} durationInFrames={38} format={s.fmt} />
                </div>
                <div
                  style={{
                    height: 12,
                    background: s.bg,
                    border: bd(3),
                    borderRadius: 8,
                    marginTop: 16,
                    width: `${interpolate(frame, [28 + i * 6, 62 + i * 6], [10, 100], {
                      extrapolateLeft: "clamp",
                      extrapolateRight: "clamp",
                    })}%`,
                  }}
                />
              </Card>
            </In>
          ))}
        </div>

        <In delay={40} style={{ width: 944, marginTop: 34 }}>
          <Card padding={26} shadow={8}>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 20, height: 190 }}>
              {days.map((d, i) => {
                const h = interpolate(frame, [40 + i * 3, 66 + i * 3], [0, 1], {
                  extrapolateLeft: "clamp",
                  extrapolateRight: "clamp",
                });
                const heights = [120, 150, 96, 176, 140, 186, 108];
                return (
                  <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
                    <div
                      style={{
                        width: "100%",
                        height: heights[i] * h,
                        background: i === 5 ? C.yellow : C.blue,
                        border: bd(3),
                        borderRadius: 10,
                        boxShadow: `4px 4px 0 ${C.ink}`,
                      }}
                    />
                    <span style={{ fontSize: 24, fontWeight: 800 }}>{d}</span>
                  </div>
                );
              })}
            </div>
          </Card>
        </In>
      </AbsoluteFill>
    </Frame>
  );
};

/* ---------------------------------------------------------------- scene 5 */
const Cta: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const pop = spring({ frame, fps, config: { damping: 12, stiffness: 100 } });

  return (
    <AbsoluteFill style={{ background: C.ink, fontFamily: FONT, color: C.white }}>
      <AbsoluteFill style={{ ...dots(C.white, 28, 0.09) }} />
      <AbsoluteFill style={{ alignItems: "center", justifyContent: "center", gap: 26 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 26, transform: `scale(${pop})` }}>
          <Pole height={170} width={34} />
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                fontSize: 108,
                fontWeight: 800,
                textTransform: "uppercase",
                letterSpacing: -4,
                lineHeight: 1,
              }}
            >
              Coba sekarang
            </div>
            <div style={{ fontSize: 34, fontWeight: 600, color: C.ink200, marginTop: 14 }}>
              Setting 5 menit, langsung bisa jualan
            </div>
          </div>
          <Pole height={170} width={34} />
        </div>

        <In delay={18} style={{ marginTop: 18 }}>
          <div
            style={{
              background: C.yellow,
              color: C.ink,
              border: bd(4),
              borderRadius: 18,
              boxShadow: `7px 7px 0 ${C.blue}`,
              padding: "24px 46px",
              fontSize: 40,
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: 2,
            }}
          >
            Chat admin · {INFO.wa}
          </div>
        </In>

        <In delay={26}>
          <Chip bg={C.cream} size={24}>
            {INFO.site}
          </Chip>
        </In>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

/* ------------------------------------------------------- rangkaian 5 scene */
export const PromoScenes: React.FC = () => {
  return (
    <>
      <Sequence from={T.title} durationInFrames={T.kasir - T.title}>
        <Title />
      </Sequence>
      <Sequence from={T.kasir} durationInFrames={T.nota - T.kasir}>
        <Kasir />
      </Sequence>
      <Sequence from={T.nota} durationInFrames={T.laporan - T.nota}>
        <Nota />
      </Sequence>
      <Sequence from={T.laporan} durationInFrames={T.cta - T.laporan}>
        <Laporan />
      </Sequence>
      <Sequence from={T.cta} durationInFrames={TOTAL - T.cta}>
        <Cta />
      </Sequence>
    </>
  );
};

/** Versi feed 1:1 — deliverable utama. */
export const PangkasinPromo: React.FC = () => {
  useOnestFonts();
  return (
    <AbsoluteFill style={{ background: C.cream }}>
      <PromoScenes />
    </AbsoluteFill>
  );
};

/** Versi story/reels 9:16 — scene 1:1 ditempatkan di tengah canvas vertikal. */
export const PangkasinStory: React.FC = () => {
  useOnestFonts();
  return (
    <AbsoluteFill style={{ background: C.ink, fontFamily: FONT }}>
      <AbsoluteFill style={{ ...dots(C.white, 30, 0.08) }} />

      <AbsoluteFill style={{ alignItems: "center", justifyContent: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 22, marginBottom: 26 }}>
          <Pole height={92} width={20} period={14} />
          <div
            style={{
              color: C.white,
              fontSize: 46,
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: 4,
            }}
          >
            {INFO.name}
          </div>
          <Pole height={92} width={20} period={14} />
        </div>

        <div
          style={{
            width: 1080,
            height: 1080,
            position: "relative",
            overflow: "hidden",
            borderRadius: 30,
            border: `5px solid ${C.cream}`,
            boxShadow: `14px 14px 0 rgba(78, 205, 196, 0.35)`,
          }}
        >
          <PromoScenes />
        </div>

        <div style={{ marginTop: 34 }}>
          <Chip bg={C.yellow} size={28}>
            Chat admin · {INFO.wa}
          </Chip>
        </div>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

export { FPS };

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
